import { useEffect, useState } from "react";

import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { ApiError, apiFetch } from "@/lib/api";
import type { Role, UserResource } from "@/lib/api-types";
import { ROLE_LABELS } from "@/lib/enum-labels";
import { useEnums } from "@/lib/enums";
import { labelFor } from "@/lib/field-labels";

/**
 * The "Add user" invite flow — mirrors `AddVendorDialog.tsx`'s own
 * established "plain one-step form" shape. `POST /users` (`app/api/users.py::
 * invite`, `Role.OWNER`) creates an invited user with no password and emails
 * a single-use acceptance link itself; this dialog only collects the three
 * fields that route actually requires (`email`, `display_name`, `role`) —
 * there is no link to copy or share.
 */
const ADD_USER_ERROR_ID = "add-user-error";

export function AddUserDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<Role | "">("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [errorField, setErrorField] = useState<string | undefined>(undefined);

  const enumsQuery = useEnums();
  const roleOptions = enumsQuery.data?.roles ?? [];

  useEffect(() => {
    if (!open) {
      setEmail("");
      setDisplayName("");
      setRole("");
      setError(undefined);
      setErrorField(undefined);
    }
  }, [open]);

  const canSubmit = email.trim() !== "" && displayName.trim() !== "" && role !== "";

  async function handleSubmit() {
    if (!canSubmit) {
      return;
    }
    setIsSubmitting(true);
    setError(undefined);
    setErrorField(undefined);
    try {
      const created = await apiFetch<UserResource>("/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), display_name: displayName.trim(), role }),
      });
      setIsSubmitting(false);
      onOpenChange(false);
      onCreated(created.id);
    } catch (submitError) {
      setError(
        submitError instanceof ApiError ? submitError.message : "Something went wrong. Please try again in a moment.",
      );
      if (submitError instanceof ApiError && submitError.field) {
        setErrorField(submitError.field);
      }
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Add a user">
      <form
        className="mt-4 flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <p className="text-body text-ink-secondary">
          They&rsquo;ll get an email with a link to set their own password and sign in.
        </p>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">
            {labelFor("display_name")} <span className="font-normal">(required)</span>
          </span>
          <Input
            autoFocus
            required
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            invalid={errorField === "display_name"}
            aria-describedby={errorField === "display_name" ? ADD_USER_ERROR_ID : undefined}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">
            {labelFor("email")} <span className="font-normal">(required)</span>
          </span>
          <Input
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            invalid={errorField === "email"}
            aria-describedby={errorField === "email" ? ADD_USER_ERROR_ID : undefined}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">
            {labelFor("role")} <span className="font-normal">(required)</span>
          </span>
          <Select
            required
            value={role}
            onChange={(event) => setRole(event.target.value as Role)}
            invalid={errorField === "role"}
            aria-describedby={errorField === "role" ? ADD_USER_ERROR_ID : undefined}
          >
            <option value="">Select a role</option>
            {roleOptions.map((option) => (
              <option key={option} value={option}>
                {ROLE_LABELS[option]}
              </option>
            ))}
          </Select>
        </label>

        {error ? (
          <p id={ADD_USER_ERROR_ID} role="alert" className="text-meta text-danger">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit || isSubmitting}>
            {isSubmitting ? "Adding…" : "Add user"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
